
import React from 'react';
import { Mail, Phone, LogIn, Clock, Heart, ExternalLink, ArrowUp } from 'lucide-react';
import { Link } from 'react-router-dom';

const Footer = () => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const currentYear = new Date().getFullYear();

  return (
    <footer className="relative bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900 text-white overflow-hidden">
      {/* Décorations géométriques */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-0 w-96 h-96 bg-blue-500 rounded-full filter blur-3xl transform -translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-pink-500 rounded-full filter blur-3xl transform translate-x-1/2 translate-y-1/2"></div>
      </div>
      
      {/* Contenu principal */}
      <div className="relative z-10 container mx-auto px-6 py-12">
        <div className="grid md:grid-cols-4 gap-8">
          {/* Brand simplifiée */}
          <div className="md:col-span-1">
            <div className="flex items-center gap-4 mb-6 group">
              <div className="bg-white p-3 rounded-lg shadow-lg group-hover:shadow-xl transition-all duration-300 group-hover:scale-105">
                <img 
                  src="/lovable-uploads/ffdc9e9e-d5a3-441c-800f-1f3ad804bb3e.png"
                  alt="Julycia Kiyal"
                  className="w-8 h-8 object-contain transition-transform duration-300 group-hover:rotate-12"
                />
              </div>
              <h3 className="text-xl font-sans font-light">
                <span className="text-white">JULYCIA</span>
                <span className="text-pink-400 ml-2 font-semibold">KIYAL</span>
              </h3>
            </div>
            
            {/* Horaires */}
            <div className="flex items-center gap-3 text-pink-200 font-light">
              <Clock size={16} />
              <span className="text-sm">Lun-Ven: 9h-18h</span>
            </div>
          </div>

          {/* Navigation */}
          <div>
            <h4 className="text-lg font-medium mb-4 relative">
              Navigation
              <span className="absolute bottom-0 left-0 w-12 h-0.5 bg-gradient-to-r from-pink-400 to-blue-400"></span>
            </h4>
            <div className="space-y-2">
              {[
                { name: 'Accueil', path: '/' },
                { name: 'Expertise', path: '/expertise' },
                { name: 'Réalisations', path: '/case-studies' },
                { name: 'Blog', path: '/blog' },
                { name: 'Speed-Inscri', path: '/speed-inscri' }
              ].map((item) => (
                <Link
                  key={item.name}
                  to={item.path}
                  className="block text-pink-200 hover:text-white transition-all duration-300 font-light group relative text-sm"
                >
                  <span className="relative z-10">{item.name}</span>
                  <span className="absolute inset-0 bg-gradient-to-r from-pink-500/20 to-blue-500/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-md -z-0"></span>
                </Link>
              ))}
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-lg font-medium mb-4 relative">
              Expertises
              <span className="absolute bottom-0 left-0 w-12 h-0.5 bg-gradient-to-r from-pink-400 to-blue-400"></span>
            </h4>
            <div className="space-y-2 text-pink-200 font-light">
              {[
                'Communication 360°',
                'Branding Expert',
                'Digital & Innovation',
                'Conseil Stratégique',
                'Identité Visuelle',
                'Stratégie de Marque'
              ].map((service) => (
                <div key={service} className="flex items-center gap-2 group">
                  <div className="w-1.5 h-1.5 bg-pink-400 rounded-full group-hover:bg-blue-400 transition-colors duration-300"></div>
                  <span className="group-hover:text-white transition-colors duration-300 text-sm">{service}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-lg font-medium mb-4 relative">
              Contact
              <span className="absolute bottom-0 left-0 w-12 h-0.5 bg-gradient-to-r from-pink-400 to-blue-400"></span>
            </h4>
            <div className="space-y-3">
              <div className="flex items-center gap-3 text-pink-200 font-light group">
                <div className="p-2 bg-pink-500/20 rounded-lg group-hover:bg-pink-500/30 transition-colors duration-300">
                  <Phone size={14} />
                </div>
                <a href="tel:+33643581540" className="hover:text-white transition-colors duration-300 text-sm">
                  (+33) 06 43 58 15 40
                </a>
              </div>
              <div className="flex items-center gap-3 text-pink-200 font-light group">
                <div className="p-2 bg-blue-500/20 rounded-lg group-hover:bg-blue-500/30 transition-colors duration-300">
                  <Mail size={14} />
                </div>
                <a href="mailto:julycia.pro@gmail.com" className="hover:text-white transition-colors duration-300 text-sm">
                  julycia.pro@gmail.com
                </a>
              </div>
              
              {/* Admin Connection Link */}
              <div className="flex items-center gap-3 text-pink-200 font-light mt-6 pt-4 border-t border-navy-700/50 group">
                <div className="p-2 bg-gradient-to-r from-pink-500/20 to-blue-500/20 rounded-lg group-hover:from-pink-500/30 group-hover:to-blue-500/30 transition-all duration-300">
                  <LogIn size={14} />
                </div>
                <Link to="/admin/login" className="hover:text-white transition-colors duration-300 flex items-center gap-2 text-sm">
                  Espace d'administration
                  <ExternalLink size={10} className="opacity-60" />
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Section créateur et copyright */}
        <div className="border-t border-navy-700/50 mt-10 pt-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            {/* Créateur du site */}
            <div className="flex items-center gap-2 text-pink-300 font-light order-2 md:order-1">
              <Heart size={14} className="text-pink-400 animate-pulse" />
              <span className="text-sm">Site réalisé par</span>
              <span className="font-medium text-white bg-gradient-to-r from-pink-400 to-blue-400 bg-clip-text text-transparent text-sm">
                Julycia Kiyal
              </span>
            </div>
            
            {/* Copyright */}
            <div className="text-center text-pink-300 font-light order-1 md:order-2">
              <p className="text-sm">&copy; {currentYear} Julycia Kiyal. Tous droits réservés.</p>
            </div>
            
            {/* Bouton retour en haut */}
            <button
              onClick={scrollToTop}
              className="p-3 bg-gradient-to-r from-pink-500 to-blue-500 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 group order-3"
              aria-label="Retour en haut"
            >
              <ArrowUp size={16} className="group-hover:animate-bounce" />
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
